// Sci-fi "loading the specialty matrix" boot sequence shown briefly before
// the Rubik specialties cube renders in the modal.
//
// Visual concept: terminal-green HUD overlay with corner crosshairs, a
// scanning gradient line sweeping vertically, and typewriter-style status
// lines that reveal one at a time. The "0%-100%" progress fills in as
// lines reveal so the user feels work is happening.
//
// Behaviour:
//   - Total duration ~2.2s; reveals 1 line every 220ms.
//   - Click anywhere → skip immediately (fires onDone).
//   - On finish, calls onDone() so the parent unmounts and renders the
//     real cube.
//   - prefers-reduced-motion: shortens the whole sequence to 250ms with
//     all lines pre-revealed.

import React, { useEffect, useRef, useState } from 'react';

interface RubikBootSequenceProps {
  onDone: () => void;
}

const TERMINAL_GREEN = '#3ddc84';
const TERMINAL_AMBER = '#fbbf24';
const TERMINAL_DIM = '#6b7280';
const MONO_FONT = "'JetBrains Mono', ui-monospace, monospace";

// Status feed — each entry is one revealed line. Order matters; the progress
// bar uses position in this array to compute %. Mix domain-loading lines
// with system chatter so it feels like a real init sequence.
type LineKind = 'sys' | 'ok' | 'domain' | 'warn' | 'final';
interface BootLine {
  kind: LineKind;
  text: string;
  /** Percentage to set the progress bar to when this line lands. */
  pct: number;
}

const LINES: BootLine[] = [
  { kind: 'sys',    text: '> attach session pid=42',                            pct: 8  },
  { kind: 'sys',    text: '> webgl.context_init',                               pct: 16 },
  { kind: 'ok',     text: '  [OK] device=apple-m1  ext=ANGLE_instanced_arrays', pct: 22 },
  { kind: 'sys',    text: '> loading specialty.matrix [6 domains]',             pct: 28 },
  { kind: 'domain', text: '  ✓ automation.testing',                             pct: 38 },
  { kind: 'domain', text: '  ✓ api+performance',                                pct: 48 },
  { kind: 'domain', text: '  ✓ cicd+devops',                                    pct: 58 },
  { kind: 'domain', text: '  ✓ security.compliance',                            pct: 68 },
  { kind: 'domain', text: '  ✓ mobile.testing',                                 pct: 78 },
  { kind: 'domain', text: '  ✓ web.automation',                                 pct: 86 },
  { kind: 'sys',    text: '> compiling 3d.mesh......',                          pct: 92 },
  { kind: 'sys',    text: '> calibrating viewport...',                          pct: 96 },
  { kind: 'final',  text: '[ READY ]   render(specialties.cube)',               pct: 100 },
];

const LINE_DELAY_MS = 160;
const FINAL_HOLD_MS = 320;

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function colorFor(kind: LineKind): string {
  switch (kind) {
    case 'sys':    return '#9ca3af';
    case 'ok':     return TERMINAL_GREEN;
    case 'domain': return TERMINAL_AMBER;
    case 'warn':   return '#f87171';
    case 'final':  return TERMINAL_GREEN;
  }
}

const RubikBootSequence: React.FC<RubikBootSequenceProps> = ({ onDone }) => {
  const [revealed, setRevealed] = useState<number>(0);
  const doneRef = useRef<boolean>(false);

  // Fire onDone exactly once even if the timer + click race.
  function finish() {
    if (doneRef.current) return;
    doneRef.current = true;
    onDone();
  }

  useEffect(() => {
    const reduced = prefersReducedMotion();
    if (reduced) {
      setRevealed(LINES.length);
      const t = setTimeout(finish, 250);
      return () => clearTimeout(t);
    }

    const timers: number[] = [];
    for (let i = 1; i <= LINES.length; i++) {
      timers.push(window.setTimeout(() => setRevealed(i), i * LINE_DELAY_MS));
    }
    timers.push(
      window.setTimeout(finish, LINES.length * LINE_DELAY_MS + FINAL_HOLD_MS),
    );
    return () => timers.forEach((t) => window.clearTimeout(t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visible = LINES.slice(0, revealed);
  const currentPct = visible.length ? visible[visible.length - 1].pct : 0;

  return (
    <div
      onClick={finish}
      role="status"
      aria-live="polite"
      aria-label="Loading specialty matrix"
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 5,
        background:
          'radial-gradient(ellipse at center, #0d1411 0%, #050807 70%, #000000 100%)',
        fontFamily: MONO_FONT,
        fontSize: '12px',
        color: TERMINAL_GREEN,
        cursor: 'pointer',
        overflow: 'hidden',
      }}
    >
      {/* Corner crosshairs — HUD frame. */}
      <Crosshair pos="tl" />
      <Crosshair pos="tr" />
      <Crosshair pos="bl" />
      <Crosshair pos="br" />

      {/* Scanning gradient line — sweeps top→bottom on repeat. CSS keyframes
          inline so we don't need an extra stylesheet. */}
      <style>{`
        @keyframes hud-scan {
          0%   { transform: translateY(-10%); opacity: 0; }
          10%  { opacity: 0.9; }
          100% { transform: translateY(110%); opacity: 0; }
        }
        @keyframes hud-grid-shift {
          0%   { background-position: 0 0; }
          100% { background-position: 0 32px; }
        }
        @keyframes hud-pulse {
          0%, 100% { opacity: 0.4; }
          50%      { opacity: 1; }
        }
        @media (prefers-reduced-motion: reduce) {
          .hud-scan, .hud-grid, .hud-pulse { animation: none !important; }
        }
      `}</style>

      <div
        className="hud-scan"
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          height: '40%',
          background:
            'linear-gradient(180deg, transparent 0%, rgba(61,220,132,0.0) 30%, rgba(61,220,132,0.18) 50%, rgba(61,220,132,0.0) 70%, transparent 100%)',
          animation: 'hud-scan 1.8s linear infinite',
          pointerEvents: 'none',
        }}
      />

      {/* Subtle terminal grid background. */}
      <div
        className="hud-grid"
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'linear-gradient(rgba(61,220,132,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(61,220,132,0.05) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
          animation: 'hud-grid-shift 4s linear infinite',
          pointerEvents: 'none',
        }}
      />

      {/* Status feed + progress bar — anchored center, tiny so it doesn't
          dominate the modal area. */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
        }}
      >
        <div
          style={{
            width: 'min(420px, 86%)',
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
            lineHeight: 1.55,
          }}
        >
          {/* Header band */}
          <div style={{ color: TERMINAL_AMBER, marginBottom: '6px' }}>
            ┌─ THANH-OS &middot; specialty.matrix &middot; v3.0 ─┐
          </div>

          {visible.map((line, idx) => (
            <div
              key={idx}
              style={{
                color: colorFor(line.kind),
                whiteSpace: 'pre',
                opacity: idx === visible.length - 1 ? 1 : 0.85,
              }}
            >
              {line.text}
            </div>
          ))}

          {/* Progress bar — width matches percent of latest line. */}
          <div
            style={{
              marginTop: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <span style={{ color: TERMINAL_DIM, width: '40px' }}>
              {String(currentPct).padStart(3, ' ')}%
            </span>
            <div
              style={{
                flex: 1,
                height: '6px',
                background: 'rgba(61,220,132,0.12)',
                border: `1px solid ${TERMINAL_GREEN}`,
                borderRadius: '2px',
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${currentPct}%`,
                  background: TERMINAL_GREEN,
                  boxShadow: '0 0 8px rgba(61,220,132,0.6)',
                  transition: 'width 180ms ease-out',
                }}
              />
            </div>
          </div>

          <div
            className="hud-pulse"
            style={{
              marginTop: '10px',
              color: TERMINAL_DIM,
              fontSize: '11px',
              textAlign: 'right',
              animation: 'hud-pulse 1.4s ease-in-out infinite',
            }}
          >
            click anywhere to skip &middot; cursor.armed
          </div>
        </div>
      </div>
    </div>
  );
};

// ---- HUD crosshair brackets ------------------------------------------------

const Crosshair: React.FC<{ pos: 'tl' | 'tr' | 'bl' | 'br' }> = ({ pos }) => {
  const size = 18;
  const offset = 14;
  const stroke = TERMINAL_GREEN;
  const common: React.CSSProperties = {
    position: 'absolute',
    width: `${size}px`,
    height: `${size}px`,
    borderColor: stroke,
    pointerEvents: 'none',
    opacity: 0.7,
  };
  switch (pos) {
    case 'tl':
      return (
        <div
          style={{
            ...common,
            top: offset,
            left: offset,
            borderTop: `2px solid ${stroke}`,
            borderLeft: `2px solid ${stroke}`,
          }}
        />
      );
    case 'tr':
      return (
        <div
          style={{
            ...common,
            top: offset,
            right: offset,
            borderTop: `2px solid ${stroke}`,
            borderRight: `2px solid ${stroke}`,
          }}
        />
      );
    case 'bl':
      return (
        <div
          style={{
            ...common,
            bottom: offset,
            left: offset,
            borderBottom: `2px solid ${stroke}`,
            borderLeft: `2px solid ${stroke}`,
          }}
        />
      );
    case 'br':
      return (
        <div
          style={{
            ...common,
            bottom: offset,
            right: offset,
            borderBottom: `2px solid ${stroke}`,
            borderRight: `2px solid ${stroke}`,
          }}
        />
      );
  }
};

export default RubikBootSequence;
